import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { NotificationsFacade } from '@layout/notifications/facades/notifications.facade';
import { TranslatePipe } from '@ngx-translate/core';
import { PanelTriggerDirective, SpinnerComponent, TooltipDirective } from '@kaseya-design-components';
import { NotificationComponent } from '@layout/notifications/components/notification/notification.component';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { Permission } from '@core/permissions/models/permission.enum';

@Component({
  selector: 'itc-notifications',
  templateUrl: 'notifications.component.html',
  styleUrl: 'notifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipDirective, TranslatePipe, NotificationComponent, SpinnerComponent, PanelTriggerDirective],
})
export class NotificationsComponent implements OnDestroy {
  private readonly notificationsFacade = inject(NotificationsFacade);
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly notificationsPanel = viewChild<ElementRef<HTMLElement>>('notificationsPanel');
  private readonly headerAction = viewChild<ElementRef<HTMLButtonElement>>('headerAction');

  readonly hasAccessToNotificationsModify = this.permissionsFacade.hasPermission(Permission.NotificationsModify);

  readonly maximumNotificationCount = 25;

  readonly notifications = computed(() => this.notificationsFacade.notifications() ?? []);
  readonly loading = this.notificationsFacade.loading;
  readonly unreadNotificationsCount = computed(() => this.notificationsFacade.unreadNotificationsCount() ?? 0);
  readonly isPanelOpen = signal(false);
  readonly closeMenu$ = new Subject<void>();

  constructor() {
    this.notificationsFacade.loadUnreadNotificationsCount();
    this.notificationsFacade.startListeningNotifications();
    this.watchPanelFocus();
  }

  closePanel(): void {
    this.closeMenu$.next();
  }

  closePanelOnFocusOut({ relatedTarget, currentTarget }: FocusEvent): void {
    if (relatedTarget && !(currentTarget as HTMLElement).contains(relatedTarget as Node)) {
      this.closePanel();
    }
  }

  onPanelVisibilityChange(isOpen: boolean): void {
    this.isPanelOpen.set(isOpen);
    this.notificationsFacade.setNotificationsVisibility(isOpen);
  }

  markAllNotificationsAsViewed(): void {
    if (!this.hasAccessToNotificationsModify()) {
      return;
    }

    this.notificationsFacade.markAllNotificationsAsViewed();
  }

  deleteAllNotifications(): void {
    if (!this.hasAccessToNotificationsModify()) {
      return;
    }

    this.notificationsFacade.deleteAllNotifications();
  }

  ngOnDestroy(): void {
    this.notificationsFacade.stopListeningNotifications();
  }

  private watchPanelFocus(): void {
    afterRenderEffect(() => {
      const panel = this.notificationsPanel()?.nativeElement;
      const focusTarget = this.headerAction()?.nativeElement ?? panel;
      this.notifications();

      const activeElement = panel?.ownerDocument.activeElement;

      if (panel && (activeElement === panel || !activeElement || !panel.contains(activeElement))) {
        focusTarget?.focus({ preventScroll: true });
      }
    });
  }
}
